import { channelFill, channelGroupMeta, channelGroupOrder } from '../../data/growthChannels';
import {
  SOURCE_META,
  SOURCE_ORDER,
  sourceMeta,
} from '../../data/reservationSources';
import type { HorizonOutlookDay } from './horizonTypes';
import { isOpenCell } from './horizonCalendar';
import type {
  ReservationSourceId,
  ServiceTimelineReservation,
} from '../../types';
import type { HorizonCourseCell } from './horizonTypes';

/**
 * Where the forward book came from.
 *
 * Channel groups and campaign names come from BigQuery on every future date,
 * at fleet scale (`channels` / `campaigns` on each outlook day). The coarser
 * widget/staff/walk-in split (`sourceMix`) remains for payloads that do not
 * carry the channel rows. GraphQL reservations still list the individual
 * bookings of one focused outlet.
 */

/** Cancelled, no-show, storno, moved, auto-dismissed. */
const CANCELLED_STATES = new Set([3, 6, 7, 14, 16]);

export type HorizonSourceRow = {
  id: string;
  label: string;
  color: string;
  covers: number;
  share: number;
};

export type HorizonCampaignRow = {
  key: string;
  label: string;
  /** Canonical source the campaign was booked through, for provenance. */
  sourceLabel: string;
  covers: number;
  parties: number;
};

export type HorizonChannelRow = {
  id: string;
  label: string;
  covers: number;
  share: number;
  /** CSS background, including the hatch used for untagged bookings. */
  fill: string;
};

export type HorizonWindowCampaign = {
  key: string;
  name: string;
  channelGroup: string;
  channelLabel: string;
  covers: number;
};

export type HorizonChannelWindow = {
  rows: HorizonChannelRow[];
  campaigns: HorizonWindowCampaign[];
  /** Share of online covers that arrived with no UTM tag. Null when there is no online book. */
  untaggedShare: number | null;
  totalCovers: number;
};

const OFFLINE = new Set(['walk_in', 'phone_staff', 'ai_phone', 'other']);

/**
 * Channel groups and campaigns across outlook days. Null when none of the
 * days carry the channel rows, so the caller can fall back to sourceMix.
 */
export function channelWindowFromDays(days: HorizonOutlookDay[]): HorizonChannelWindow | null {
  const covers = new Map<string, number>();
  const campaigns = new Map<string, HorizonWindowCampaign>();
  let sawChannels = false;
  for (const day of days) {
    if (!day.channels) continue;
    sawChannels = true;
    for (const [id, value] of Object.entries(day.channels)) {
      if (!Number.isFinite(value) || value <= 0) continue;
      covers.set(id, (covers.get(id) ?? 0) + value);
    }
    for (const campaign of day.campaigns ?? []) {
      if (!campaign.name || campaign.covers <= 0) continue;
      const key = `${campaign.channelGroup}:${campaign.name}`;
      const existing = campaigns.get(key);
      if (existing) existing.covers += campaign.covers;
      else {
        campaigns.set(key, {
          key,
          name: campaign.name,
          channelGroup: campaign.channelGroup,
          channelLabel: channelGroupMeta(campaign.channelGroup).label,
          covers: campaign.covers,
        });
      }
    }
  }
  if (!sawChannels || covers.size === 0) return null;
  const totalCovers = [...covers.values()].reduce((sum, value) => sum + value, 0);
  const online = [...covers.entries()]
    .filter(([id]) => !OFFLINE.has(id))
    .reduce((sum, [, value]) => sum + value, 0);
  let rank = 0;
  const rows = [...covers.entries()]
    .map(([id, value]) => ({
      id,
      label: channelGroupMeta(id).label,
      covers: value,
      share: totalCovers > 0 ? (value / totalCovers) * 100 : 0,
    }))
    .sort(
      (a, b) =>
        Number(a.id === 'untagged') - Number(b.id === 'untagged') ||
        b.covers - a.covers ||
        channelGroupOrder(a.id) - channelGroupOrder(b.id),
    )
    .map(row => ({
      ...row,
      fill: channelFill(row.id, row.id === 'untagged' ? 0 : rank++),
    }));
  return {
    rows,
    campaigns: [...campaigns.values()].sort((a, b) => b.covers - a.covers).slice(0, 8),
    untaggedShare: online > 0 ? (covers.get('untagged') ?? 0) / online : null,
    totalCovers,
  };
}

export type HorizonSourceSummary = {
  rows: HorizonSourceRow[];
  totalCovers: number;
  /** Plain-language read of the mix, or null when there is nothing to say. */
  headline: string | null;
};

function isCancelled(r: ServiceTimelineReservation): boolean {
  return CANCELLED_STATES.has(r.state);
}

function sourceLabel(id: string): string {
  const known = SOURCE_META[id as ReservationSourceId];
  if (known) return known.label;
  // Raw enums must never reach a manager's screen.
  return id
    .replace(/[_-]+/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/^./, c => c.toUpperCase());
}

function orderIndex(id: string): number {
  const i = SOURCE_ORDER.indexOf(id as ReservationSourceId);
  return i < 0 ? SOURCE_ORDER.length : i;
}

function toRows(totals: Map<string, number>): HorizonSourceRow[] {
  const totalCovers = [...totals.values()].reduce((s, v) => s + v, 0);
  if (totalCovers <= 0) return [];
  return [...totals.entries()]
    .filter(([, covers]) => covers > 0)
    .map(([id, covers]) => ({
      id,
      label: sourceLabel(id),
      color: sourceMeta(id as ReservationSourceId).color,
      covers,
      share: (covers / totalCovers) * 100,
    }))
    .sort(
      (a, b) => b.covers - a.covers || orderIndex(a.id) - orderIndex(b.id),
    );
}

function headlineFor(rows: HorizonSourceRow[], totalCovers: number): string | null {
  if (rows.length === 0 || totalCovers <= 0) return null;
  const top = rows[0];
  const pct = Math.round(top.share);
  if (rows.length === 1) {
    return `Every one of the ${Math.round(totalCovers)} covers booked came through ${top.label.toLowerCase()}.`;
  }
  const second = rows[1];
  return `${top.label} brought ${pct}% of the covers booked, ${second.label.toLowerCase()} ${Math.round(second.share)}%.`;
}

/**
 * Canonical source split across the given days, from the counted forward book.
 * Works at any scope because it reads the BigQuery mix already on each cell.
 */
export function sourceSummaryFromCells(
  cells: HorizonCourseCell[],
  dates: Set<string>,
  outletId: string | null,
): HorizonSourceSummary {
  const totals = new Map<string, number>();
  let heroOnBooks = 0;
  for (const cell of cells) {
    if (!dates.has(cell.serviceDate)) continue;
    if (outletId && cell.outletId !== outletId) continue;
    if (!isOpenCell(cell)) continue;
    const booked = Number(cell.onBooks?.value);
    if (Number.isFinite(booked) && booked > 0) heroOnBooks += booked;
    for (const [id, covers] of Object.entries(cell.sourceMix ?? {})) {
      const value = Number(covers);
      if (!Number.isFinite(value) || value <= 0) continue;
      totals.set(id, (totals.get(id) ?? 0) + value);
    }
  }
  let rows = toRows(totals);
  let totalCovers = rows.reduce((s, r) => s + r.covers, 0);
  if (heroOnBooks > 0 && totalCovers > heroOnBooks) {
    const scale = heroOnBooks / totalCovers;
    totalCovers = heroOnBooks;
    rows = rows.map(r => {
      const covers = r.covers * scale;
      return {
        ...r,
        covers,
        share: totalCovers > 0 ? (covers / totalCovers) * 100 : 0,
      };
    });
  }
  return { rows, totalCovers, headline: headlineFor(rows, totalCovers) };
}

/** Canonical source split for one day's actual reservations. */
export function sourceSummaryFromReservations(
  reservations: ServiceTimelineReservation[],
): HorizonSourceSummary {
  const totals = new Map<string, number>();
  for (const r of reservations) {
    if (isCancelled(r) || r.peopleCount <= 0) continue;
    const id = r.channel || 'other';
    totals.set(id, (totals.get(id) ?? 0) + r.peopleCount);
  }
  const rows = toRows(totals);
  const totalCovers = rows.reduce((s, r) => s + r.covers, 0);
  return { rows, totalCovers, headline: headlineFor(rows, totalCovers) };
}

/**
 * Named campaigns and referrers behind the book, biggest first. Only bookings
 * that actually carry attribution appear — an untagged booking is reported as
 * untagged rather than folded into a campaign it may not belong to.
 */
export function campaignRows(
  reservations: ServiceTimelineReservation[],
  limit = 6,
): { rows: HorizonCampaignRow[]; untaggedCovers: number } {
  const byKey = new Map<string, HorizonCampaignRow>();
  let untaggedCovers = 0;

  for (const r of reservations) {
    if (isCancelled(r) || r.peopleCount <= 0) continue;
    const campaign = (r.utmCampaign || r.campaign || '').trim();
    const referrer = (r.utmSource || '').trim();
    const key = campaign || referrer;
    if (!key) {
      untaggedCovers += r.peopleCount;
      continue;
    }
    const existing = byKey.get(key);
    if (existing) {
      existing.covers += r.peopleCount;
      existing.parties += 1;
      continue;
    }
    byKey.set(key, {
      key,
      label: campaign || `Referred by ${referrer}`,
      sourceLabel: sourceLabel(r.channel || 'other'),
      covers: r.peopleCount,
      parties: 1,
    });
  }

  const rows = [...byKey.values()]
    .sort((a, b) => b.covers - a.covers || a.label.localeCompare(b.label))
    .slice(0, limit);
  return { rows, untaggedCovers };
}
